import { type SkuId, type StoreName } from '@bluebrick/discounts-service-module'
import type { DropdownItemData, DropdownRoot, NotificationType, TableItem } from '@bluebrick/saas-ui'
import type { MbscCalendarEvent, MbscResource } from '@mobiscroll/vue'
import { type DateTime } from 'luxon'
import { type z } from 'zod'
import {
	type recommendedDatesSchema,
	type eventDataSchema,
	type historyEventSchema,
	type productSchema,
	type recommendationsSchema,
	type skuDataSchema,
	type storeDataSchema,
	type noteSchema,
	type actionsSchema,
	type storeSchema,
	type discountConfigSchema,
	type discountTimeRuleSchema
} from './schema'
import { type LastScrapeDate, type PortalsStatus } from '@/features/dataHealth/types'
import { type SourceAccount } from '@/features/discountSetters/types'

export type Nullable<T> = T | null

export type LengthInDays = number

export enum Portal {
	EPIC = 'EPIC',
	GOG = 'GOG',
	HUMBLE = 'HUMBLE',
	MICROSOFT = 'MICROSOFT',
	NINTENDO = 'NINTENDO',
	META = 'META',
	STEAM = 'STEAM',
	PLAYSTATION = 'PLAYSTATION',
	APPLE = 'APPLE',
	GOOGLE = 'GOOGLE'
}

export enum EventType {
	Discount = 'discount',
	Opportunity = 'opportunity',
	StoreOpportunity = 'store_discount',
	Cooldown = 'cooldown',
	NoGo = 'no-go zone',
	LocalDraft = 'local-draft'
}

type EventPlannerIncomingEventPayloads = {
	SCRAPERS_STATUS: PortalsStatus
	LAST_SCRAPE_DATES_CHANGED: LastScrapeDate[]
	SHOULD_OPEN_ACTIONS_PANEL: boolean
	IDLE_STATE_CHANGED: boolean
	SOURCE_ACCOUNTS: SourceAccount[]
}

type EventPlannerOutgoingEventPayloads = {
	GET_SCRAPERS_STATUS: undefined
	FIX_PORTAL_REDIRECT: Portal
	OPEN_SOURCES_TAB: undefined
	OPEN_DASHBOARD: {
		id: string
		filters?: Record<string, unknown>
	}
	ACTIONS_UPDATED: {
		unplannedEventsCount: number
		unscheduledEventsCount: number
	}
}

type IFrameIncomingEventPayloads = {
	TOKEN_HAS_CHANGED: string
	DASHBOARD_DISCOUNT_ANALYTICS_ENABLED: boolean
	INSTALLATION_ID_UPDATED: string
} & EventPlannerIncomingEventPayloads

type NotifyEvent = {
	message: string
	type: NotificationType
}

type IFrameOutgoingEventPayloads = {
	LOADED: string
	GET_TOKEN: string
	VERSION: string
	NOTIFY: NotifyEvent
	OPEN_CHAT: string
} & EventPlannerOutgoingEventPayloads

export type IFrameIncomingEvent = keyof IFrameIncomingEventPayloads

type IFrameOutgoingEvent = keyof IFrameOutgoingEventPayloads

export type IFrameOutgoingPostFunction = <T extends IFrameOutgoingEvent>(type: T, payload?: IFrameOutgoingEventPayloads[T]) => void

export type IFrameIncomingPostMessageCallbacks = Partial<{
	[K in IFrameIncomingEvent]: (payload: IFrameIncomingEventPayloads[K]) => void
}>

export type IFrameIncomingPostMessageData<T extends IFrameIncomingEvent = IFrameIncomingEvent> = {
	data?: { type: T; payload: IFrameIncomingEventPayloads[T] }
}

export enum SidebarContent {
	EVENT_SETUP = 'EVENT_SETUP',
	PRODUCT_HISTORY = 'PRODUCT_HISTORY',
	GUIDE = 'GUIDE',
	EVENT_TYPE_LEGEND = 'EVENT_TYPE_LEGEND',
	FAVOURITE_SKUS = 'FAVOURITE_SKUS',
	SALE_DAYS_SUMMARY = 'SALE_DAYS_SUMMARY',
	ADVISED_ACTIONS = 'ADVISED_ACTIONS'
}

export const allowedMultipleSidebars: Partial<Record<SidebarContent, SidebarContent[]>> = {
	[SidebarContent.EVENT_SETUP]: [SidebarContent.PRODUCT_HISTORY],
	[SidebarContent.PRODUCT_HISTORY]: [SidebarContent.EVENT_SETUP]
}

export type EventData = z.infer<typeof eventDataSchema>

export type EventDataWithStore = EventData & {
	storeName?: StoreName
	hidden?: boolean
}

export type TimelineEvent = MbscCalendarEvent & {
	rawEvent: EventDataWithStore
}

export type Product = z.infer<typeof productSchema>

export type SkuData = z.infer<typeof skuDataSchema>

export type SkuDataWithAccountName = SkuData & {
	account_name?: string
}

export type StoreData = z.infer<typeof storeDataSchema>

export type Store = z.infer<typeof storeSchema>

export interface SkuOption {
	storeId: string
	humanName: string
	uniqueSkuId: SkuId
	discountDepth: number
}

export type HistoryEvent = z.infer<typeof historyEventSchema>

export interface HistoryTableItem extends TableItem {
	eventName: string
	startDate: DateTime
	endDate: DateTime
	discountDepth: number
	units: number
	baseline: number
	revenue: number
	uplift: number
	major: boolean
	promoLength: number
	upliftScore: number
}

export type ProductDropdownData = DropdownRoot<never, DropdownItemData<SkuData>>

export interface UpsertEventRequestBody {
	start_date?: string
	end_date?: string
	discount_depth: number
	opportunity_origin?: EventData
	name?: string
	major?: boolean
	triggers_cooldown?: boolean
	notes: Note[]
}

export type ProductResource = MbscResource & {
	skuData: SkuData
}

export type WithDates = {
	start_date: DateTime | null
	end_date: DateTime | null
}

export type Recommendations = z.infer<typeof recommendationsSchema>

export type RecommendedDates = z.infer<typeof recommendedDatesSchema>

export type Note = z.infer<typeof noteSchema>
export type Actions = z.infer<typeof actionsSchema>

export const NONE_PRODUCT_NAME = 'None'
export const UNASSIGNED_PRODUCT_NAME = 'Unassigned'

export type DiscountConfig = z.infer<typeof discountConfigSchema>
export type DiscountTimeRule = z.infer<typeof discountTimeRuleSchema>
